/* eslint-disable react/jsx-one-expression-per-line */
/* eslint-disable max-lines-per-function */
/* eslint-disable react/jsx-no-useless-fragment */
import React, { useEffect, useMemo } from 'react'
import Dialog from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import {
  CircularProgress,
  Grid,
  IconButton,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
} from '@mui/material'
import { StateMethods, useHookstate } from '@hookstate/core'
import CloseIcon from '@mui/icons-material/Close'
import { useLang } from '~/hooks/useLang'
import dayjs from 'dayjs'
import { getWarehouseById } from '../../services'
import IWarehouse from '../../interfaces/IWarehouse'

interface IWarehouseUser {
  readonly id: number
  readonly is_operator: number
  readonly is_manager: number
  readonly user: {
    readonly id: number
    readonly representation_name?: string
    readonly registration?: string | null
    readonly name?: string
  }
}

export interface ViewMoreProps {
  readonly open: StateMethods<boolean>
  readonly itemId: number | null
  readonly handleClose: () => void
}

export default function ViewMore({
  open,
  itemId,
  handleClose,
}: ViewMoreProps) {
  const { translate } = useLang()
  const warehouse = useHookstate<IWarehouse | null>(null)
  const loading = useHookstate<boolean>(false)

  useEffect(() => {
    if (itemId) {
      loading.set(true)
      getWarehouseById(itemId)
        .then((data) => {
          warehouse.set(data)
        })
        .finally(() => loading.set(false))
    }
  }, [itemId])

  const managers = useMemo(() => {
    const warehouseData = warehouse.get()
    if (!warehouseData?.warehouse_users) return []
    return warehouseData.warehouse_users.filter((wu) => wu.is_manager === 1)
  }, [warehouse.get()])

  const operators = useMemo(() => {
    const warehouseData = warehouse.get()
    if (!warehouseData?.warehouse_users) return []
    return warehouseData.warehouse_users.filter((wu) => wu.is_operator === 1)
  }, [warehouse.get()])

  const clickOutModalClose = (
    _event: unknown,
    reason: 'backdropClick' | 'escapeKeyDown'
  ) => {
    if (reason !== 'backdropClick') {
      open.set(false)
    }
  }

  return (
    <Dialog
      fullWidth
      maxWidth={'lg'}
      open={open.get()}
      onClose={clickOutModalClose}
      disableEscapeKeyDown={true}
    >
      <DialogTitle
        display={'flex'}
        alignItems={'center'}
        justifyContent={'space-between'}
      >
        {translate('Warehouse Details')}
        <IconButton aria-label="close" onClick={() => handleClose()}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {loading.get() ? (
          <Grid
            item
            xs={12}
            display={'flex'}
            sx={{ justifyContent: 'center', p: 4 }}
          >
            <CircularProgress />
          </Grid>
        ) : (
          <Grid container>

            <Grid item xs={3}>
              <Typography variant="subtitle1" fontWeight={'bold'}>
                {translate('Warehouse Name')}:
              </Typography>
              <Typography variant="subtitle2">
                {warehouse.get()?.name ?? translate('Uninformed')}
              </Typography>
            </Grid>

            <Grid item xs={3}>
              <Typography variant="subtitle1" fontWeight={'bold'}>
                {translate('Agency:name')}:
              </Typography>
              <Typography variant="subtitle2">
                {warehouse.get()?.agency
                  ? `[${warehouse.get()?.agency?.acronym}] ${
                      warehouse.get()?.agency?.name
                    }`
                  : translate('Uninformed')}
              </Typography>
            </Grid>

            <Grid item xs={3}>
              <Typography variant="subtitle1" fontWeight={'bold'}>
                {translate('Active')}:
              </Typography>
              <Typography variant="subtitle2">
                {!!warehouse.get()?.is_active
                  ? translate('Yes')
                  : translate('No')}
              </Typography>
            </Grid>

            <Grid item xs={3}>
              <Typography variant="subtitle1" fontWeight={'bold'}>
                {translate('created_at')}:
              </Typography>
              <Typography variant="subtitle2">
                {warehouse.get()?.created_at
                  ? dayjs(warehouse.get()?.created_at).format(
                      'DD/MM/YYYY HH:mm:ss'
                    )
                  : translate('Uninformed')}
              </Typography>
            </Grid>

            {/* Tabela de Gerentes */}
            <Grid item xs={12} sx={{ mt: 3 }}>
              <Typography variant="h6" fontWeight={'bold'} sx={{ mb: 2 }}>
                {translate('Managers')} <Chip label={managers.length} size="small" color="primary" sx={{ ml: 1 }} />
              </Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 300 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell width="50%">
                        <strong>{translate('Name')}</strong>
                      </TableCell>
                      <TableCell width="50%">
                        <strong>{translate('Registration')}</strong>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {managers.length > 0 ? (
                      managers.map((warehouseUser: IWarehouseUser) => (
                        <TableRow key={warehouseUser.id}>
                          <TableCell>
                            {warehouseUser.user?.representation_name || warehouseUser.user?.name || '-'}
                          </TableCell>
                          <TableCell>
                            {warehouseUser.user?.registration || '-'}
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={2} align="center">
                          {translate('No managers found')}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>

            {/* Tabela de Operadores */}
            <Grid item xs={12} sx={{ mt: 3 }}>
              <Typography variant="h6" fontWeight={'bold'} sx={{ mb: 2 }}>
                {translate('Operators')} <Chip label={operators.length} size="small" color="secondary" sx={{ ml: 1 }} />
              </Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 300 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell width="50%">
                        <strong>{translate('Name')}</strong>
                      </TableCell>
                      <TableCell width="50%">
                        <strong>{translate('Registration')}</strong>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {operators.length > 0 ? (
                      operators.map((warehouseUser: IWarehouseUser) => (
                        <TableRow key={warehouseUser.id}>
                          <TableCell>
                            {warehouseUser.user?.representation_name || warehouseUser.user?.name || '-'}
                          </TableCell>
                          <TableCell>
                            {warehouseUser.user?.registration || '-'}
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={2} align="center">
                          {translate('No operators found')}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          </Grid>
        )}
      </DialogContent>
    </Dialog>
  )
}
